import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ArrowUp,
  ArrowDown,
  Check,
  Eye,
  ListVideo,
} from "lucide-react";
import { ImageUpload, mediaSrc } from "@/components/admin/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const req = (method, path, data) => axios({ method, url: `${API}${path}`, data, withCredentials: true });

const input =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30";
const btnDark =
  "inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2 text-xs font-semibold";
const btnGhost =
  "inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs";

const emptyVideo = { title: "", description: "", video_source: "youtube", video_link: "" };

function VideoForm({ initial, onSave, onCancel, testid }) {
  const [form, setForm] = useState(initial || emptyVideo);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    try {
      const { data } = await req("post", "/admin/training/preview", {
        video_source: form.video_source,
        video_link: form.video_link,
      });
      setPreview(data.embed_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid video link");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await onSave(form);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 dark:border-white/10 p-4 space-y-3 bg-slate-50/60 dark:bg-white/[0.02]">
      <input
        className={input}
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Video title (e.g. Day 1 - Introduction)"
        data-testid={`${testid}-title`}
      />
      <textarea
        rows={2}
        className={input}
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Short description"
        data-testid={`${testid}-description`}
      />
      <div className="grid sm:grid-cols-[140px_1fr] gap-2">
        <select
          className={input}
          value={form.video_source}
          onChange={(e) => setForm({ ...form, video_source: e.target.value })}
          data-testid={`${testid}-source`}
        >
          <option value="youtube">YouTube</option>
          <option value="drive">Google Drive</option>
        </select>
        <input
          className={input}
          value={form.video_link}
          onChange={(e) => setForm({ ...form, video_link: e.target.value })}
          placeholder={form.video_source === "youtube" ? "https://youtube.com/watch?v=…" : "https://drive.google.com/file/d/…/view"}
          data-testid={`${testid}-link`}
        />
      </div>
      {preview && (
        <div className="aspect-video w-full rounded-xl overflow-hidden border border-slate-200 dark:border-white/10">
          <iframe src={preview} title="Preview" allowFullScreen className="h-full w-full" />
        </div>
      )}
      <div className="flex items-center gap-2">
        <button type="button" onClick={doPreview} className={btnGhost} data-testid={`${testid}-preview`}>
          <Eye className="h-3 w-3" /> Preview
        </button>
        <button type="button" onClick={save} disabled={busy} className={btnDark} data-testid={`${testid}-save`}>
          <Check className="h-3 w-3" /> {busy ? "Saving…" : "Save video"}
        </button>
        <button type="button" onClick={onCancel} className={btnGhost} data-testid={`${testid}-cancel`}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function TrainingManager() {
  const [list, setList] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [meta, setMeta] = useState({ title: "", description: "", cover: "", status: "published" });
  const [adding, setAdding] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await req("get", "/admin/training-series");
      setList(data);
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const series = list.find((s) => s.id === openId) || null;

  const openSeries = (s) => {
    setOpenId(s.id);
    setMeta({ title: s.title, description: s.description || "", cover: s.cover || "", status: s.status });
    setAdding(false);
    setEditingVideo(null);
  };

  const createSeries = async () => {
    const title = window.prompt("Series name (e.g. 7 Days Freelancing Training)");
    if (!title) return;
    const { data } = await req("post", "/admin/training-series", {
      title,
      description: "",
      cover: "",
      status: "draft",
    });
    await load();
    openSeries(data);
    toast.success("Series created — add your videos");
  };

  const saveMeta = async () => {
    try {
      await req("put", `/admin/training-series/${openId}`, meta);
      await load();
      toast.success("Series updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save series");
    }
  };

  const deleteSeries = async (s) => {
    if (!window.confirm(`Delete "${s.title}" and its ${s.video_count} videos?`)) return;
    await req("delete", `/admin/training-series/${s.id}`);
    setOpenId(null);
    await load();
    toast.success("Series deleted");
  };

  const addVideo = async (form) => {
    try {
      await req("post", `/admin/training-series/${openId}/videos`, form);
      await load();
      setAdding(false);
      toast.success("Video added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add video");
    }
  };

  const updateVideo = async (form) => {
    try {
      await req("put", `/admin/training-series/${openId}/videos/${editingVideo.id}`, form);
      await load();
      setEditingVideo(null);
      toast.success("Video updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update video");
    }
  };

  const removeVideo = async (v) => {
    if (!window.confirm(`Remove "${v.title}"?`)) return;
    await req("delete", `/admin/training-series/${openId}/videos/${v.id}`);
    await load();
  };

  const move = async (v, direction) => {
    await req("post", `/admin/training-series/${openId}/videos/${v.id}/move`, { direction });
    await load();
  };

  if (!series) {
    return (
      <div data-testid="admin-training-manager" className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {list.length} training series. Each series is a playlist students watch in order.
          </p>
          <button type="button" onClick={createSeries} className={btnDark} data-testid="create-series">
            <Plus className="h-3.5 w-3.5" /> New series
          </button>
        </div>
        {loading && <div className="text-sm text-slate-400">Loading…</div>}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((s) => (
            <div
              key={s.id}
              data-testid={`series-card-${s.id}`}
              className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-hidden"
            >
              <div className="h-32 bg-slate-100 dark:bg-white/5">
                {s.cover ? (
                  <img src={mediaSrc(s.cover)} alt={s.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full grid place-items-center text-slate-400">
                    <ListVideo className="h-6 w-6" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold leading-snug">{s.title}</h3>
                  <span className={`text-[10px] font-semibold uppercase ${s.status === "published" ? "text-emerald-500" : "text-amber-500"}`}>
                    {s.status}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500 line-clamp-2">{s.description || "No description yet."}</p>
                <p className="mt-2 text-[11px] text-slate-400">{s.video_count} videos</p>
                <div className="mt-3 flex items-center gap-2">
                  <button type="button" onClick={() => openSeries(s)} className={btnDark} data-testid={`manage-series-${s.id}`}>
                    <Pencil className="h-3 w-3" /> Manage
                  </button>
                  <button type="button" onClick={() => deleteSeries(s)} className={btnGhost} data-testid={`delete-series-${s.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {!loading && list.length === 0 && (
            <div className="text-sm text-slate-400">No series yet — create your first one.</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="series-editor" className="space-y-5">
      <button type="button" onClick={() => setOpenId(null)} className={btnGhost} data-testid="series-back">
        <ChevronLeft className="h-3 w-3" /> All series
      </button>

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-5">
        <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 space-y-3">
          <h3 className="font-display text-base font-semibold tracking-tight">Series settings</h3>
          <input
            className={input}
            value={meta.title}
            onChange={(e) => setMeta({ ...meta, title: e.target.value })}
            placeholder="Series name"
            data-testid="series-title"
          />
          <textarea
            rows={3}
            className={input}
            value={meta.description}
            onChange={(e) => setMeta({ ...meta, description: e.target.value })}
            placeholder="Series description"
            data-testid="series-description"
          />
          <ImageUpload
            value={meta.cover}
            onChange={(url) => setMeta({ ...meta, cover: url })}
            folder="series"
            label="Series cover photo"
            testid="series-cover-upload"
          />
          <select
            className={input}
            value={meta.status}
            onChange={(e) => setMeta({ ...meta, status: e.target.value })}
            data-testid="series-status"
          >
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          <button type="button" onClick={saveMeta} className={btnDark} data-testid="series-save">
            <Check className="h-3 w-3" /> Save series
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100 dark:border-white/5">
            <h3 className="font-display text-base font-semibold tracking-tight">
              Videos in this series ({series.video_count})
            </h3>
            <button type="button" onClick={() => { setAdding(true); setEditingVideo(null); }} className={btnDark} data-testid="add-series-video">
              <Plus className="h-3 w-3" /> Add video
            </button>
          </div>

          <div className="p-4 space-y-3">
            {adding && (
              <VideoForm initial={emptyVideo} onSave={addVideo} onCancel={() => setAdding(false)} testid="new-video-form" />
            )}

            {(series.videos || []).map((v, i) =>
              editingVideo?.id === v.id ? (
                <VideoForm
                  key={v.id}
                  initial={{
                    title: v.title,
                    description: v.description || "",
                    video_source: v.video_source,
                    video_link: v.video_link || "",
                  }}
                  onSave={updateVideo}
                  onCancel={() => setEditingVideo(null)}
                  testid={`edit-video-form-${v.id}`}
                />
              ) : (
                <div
                  key={v.id}
                  data-testid={`series-video-${v.id}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/10 p-3"
                >
                  <span className="h-7 w-7 shrink-0 grid place-items-center rounded-lg bg-slate-100 dark:bg-white/5 text-[11px] font-bold">
                    {v.order}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{v.title}</div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {v.video_source === "youtube" ? "YouTube" : "Google Drive"} · {v.description || "no description"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => move(v, "up")} disabled={i === 0} className={btnGhost} data-testid={`move-up-${v.id}`}>
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(v, "down")}
                      disabled={i === (series.videos || []).length - 1}
                      className={btnGhost}
                      data-testid={`move-down-${v.id}`}
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => { setEditingVideo(v); setAdding(false); }} className={btnGhost} data-testid={`edit-video-${v.id}`}>
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => removeVideo(v)} className={btnGhost} data-testid={`delete-video-${v.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ),
            )}

            {(series.videos || []).length === 0 && !adding && (
              <div className="text-sm text-slate-400">No videos yet — add the first one.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
